import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { CompanyDocumentTable } from "@/components/crud/table/CompanyDocumentTable";
import { CompanyDocumentFormSheet } from "@/components/crud/forms/CompanyDocumentFormSheet";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { toast } from "sonner";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import PageHeader from "@/components/reusable-components/header/page-header";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import { useDebounce } from "@/hooks/use-debounce";
import useFetchCompanyDocuments, {
  type CompanyDocumentOrdering,
} from "@/hooks/api/document/company-document/useFetchCompanyDocuments";
import useCreateCompanyDocument from "@/hooks/api/document/company-document/useCreateCompanyDocument";
import useUpdateCompanyDocument from "@/hooks/api/document/company-document/useUpdateCompanyDocument";
import useDeleteCompanyDocument from "@/hooks/api/document/company-document/useDeleteCompanyDocument";
import type { CompanyDocument, CompanyDocumentPayload } from "@/types/document";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

export const Route = createFileRoute("/admin/_layout/documents/company")({
  component: CompanyDocumentsPage,
});

function CompanyDocumentsPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "document.company.manage");
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        title: SortDirection;
        created_at: SortDirection;
      },
      CompanyDocumentOrdering
    >({
      title: null,
      created_at: null,
    });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editingDocument, setEditingDocument] =
    useState<CompanyDocument | null>(null);
  const [documentToDelete, setDocumentToDelete] =
    useState<CompanyDocument | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 500);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const companyDocumentsQuery = useFetchCompanyDocuments({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
  });
  const companyDocuments = companyDocumentsQuery.data?.results ?? [];
  const count = companyDocumentsQuery.data?.count ?? 0;

  const createCompanyDocument = useCreateCompanyDocument();
  const updateCompanyDocument = useUpdateCompanyDocument();
  const deleteCompanyDocument = useDeleteCompanyDocument();

  const isMutating =
    createCompanyDocument.isPending ||
    updateCompanyDocument.isPending ||
    deleteCompanyDocument.isPending;
  const isLoading = companyDocumentsQuery.isPending || isMutating;
  const isFetching = companyDocumentsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, setPage]);

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setOrdering({
      title: null,
      created_at: null,
    });
    setPage(1);
  };

  const handleCreate = () => {
    setEditingDocument(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (doc: CompanyDocument) => {
    setEditingDocument(doc);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (doc: CompanyDocument) => {
    setEditingDocument(doc);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (doc: CompanyDocument) => {
    setDocumentToDelete(doc);
    setIsDeleteDialogOpen(true);
  };

  const onCreate = async (data: CompanyDocumentPayload) => {
    await createCompanyDocument.mutateAsync(data);
    toast.success("Document created successfully!");
    setIsSheetOpen(false);
  };

  const onEdit = async (id: number, data: CompanyDocumentPayload) => {
    await updateCompanyDocument.mutateAsync({ id, payload: data });
    toast.success("Document updated successfully!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      await deleteCompanyDocument.mutateAsync({ id: documentToDelete.id });
      toast.success("Document deleted successfully!");
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Company Documents"
          description="Manage public and private documents for the entire company."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Add Document"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search documents..."
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </FiltersPanel>

        <CompanyDocumentTable
          documents={companyDocuments}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <CompanyDocumentFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={() => setIsSheetOpen(false)}
          onCreate={onCreate}
          onEdit={onEdit}
          editingDocument={editingDocument}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Document"
          description={`Are you sure you want to delete the document "${documentToDelete?.title}"? This action cannot be undone.`}
          itemName={documentToDelete?.title || ""}
          isLoading={deleteCompanyDocument.isPending}
          destructive={true}
        />
      </div>
    </SidebarInset>
  );
}
